import styled, { keyframes } from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

// Version 4, best-of-both: V2's animated scorecard (count-up donut + filling
// bars) for the scores, V3's visual widget gallery (hero images) for the content,
// plus V2's impact meters on the cards. Self-contained; V1/V2/V3 untouched.

const STATUS_COLOR = {
  good: '#16a34a',
  fair: '#d28100',
  poor: '#dc2626',
} as const;

export const StyledHero = styled.section`
  background: linear-gradient(140deg, #0b0c3a 0%, #010101 60%);
  color: ${CSSVars.white};
  padding: 140px 20px 90px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% -10%, rgba(76, 79, 255, 0.35), transparent 55%);
    pointer-events: none;
  }
  > * { position: relative; z-index: 1; }

  h1 {
    font-size: 1.875rem;
    font-weight: 300;
    margin-bottom: 18px;
    line-height: 1.15;
    color: ${CSSVars.white};

    .highlight {
      font-weight: 700;
      background: linear-gradient(90deg, #a5a8ff, #ff979c);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
  p.sub {
    font-size: ${CSSVars.fontSizeMD};
    color: rgba(255, 255, 255, 0.8);
    max-width: 620px;
    margin: 0 auto 36px;
    line-height: 1.7;
    font-weight: 300;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 165px 40px 110px;
    h1 { font-size: ${CSSVars.fontSize2XL}; }
    p.sub { font-size: ${CSSVars.fontSizeLG}; }
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 185px 40px 120px;
    h1 { font-size: ${CSSVars.fontSize3XL}; }
  }
`;

export const StyledForm = styled.form`
  max-width: 620px;
  margin: 0 auto;

  .input-row {
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      flex-direction: row;
      background: ${CSSVars.white};
      border-radius: 12px;
      padding: 8px;
      box-shadow: 0 8px 40px rgba(0, 0, 0, 0.35);
    }
  }
  input {
    flex: 1;
    width: 100%;
    padding: 15px 18px;
    font-size: ${CSSVars.fontSizeMD};
    border-radius: 10px;
    border: none;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    font-family: ${CSSVars.secondaryFont};
  }
  button {
    padding: 15px 28px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.3s, transform 0.1s;
    font-family: ${CSSVars.primaryFont};
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
    &:disabled { opacity: 0.6; cursor: default; }
  }
  .error {
    margin-top: 14px;
    color: #ffd2d2;
    font-size: ${CSSVars.fontSizeSM};
  }
`;

export const StyledHint = styled.p`
  margin-top: 22px;
  font-size: ${CSSVars.fontSizeSM};
  color: rgba(255, 255, 255, 0.55);
  font-weight: 300;
`;

export const StyledTrustBadges = styled.div`
  margin-top: 30px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px 24px;
  align-items: center;
  justify-content: center;

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 300;
    color: rgba(255, 255, 255, 0.8);

    strong { color: ${CSSVars.white}; font-weight: 700; }
  }

  .trustpilot {
    display: inline-flex;
    align-items: center;
    background: ${CSSVars.white};
    padding: 7px 12px;
    border-radius: 8px;
    line-height: 0;
    transition: transform 0.2s, box-shadow 0.2s;

    img { display: block; }

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
    }
  }
`;

const spin = keyframes`to { transform: rotate(360deg); }`;

export const StyledBody = styled.div`
  background: ${CSSVars.gray50};
  padding: 60px 20px 90px;
  scroll-margin-top: 60px;

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 80px 40px 110px;
  }
`;

export const StyledLoader = styled.div`
  max-width: 560px;
  margin: 0 auto;
  text-align: center;
  color: ${CSSVars.gray600};

  .ring {
    width: 54px; height: 54px;
    margin: 0 auto 22px;
    border: 4px solid ${CSSVars.gray200};
    border-top-color: ${CSSVars.blue500};
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
  p { font-size: ${CSSVars.fontSizeMD}; }
  .url { color: ${CSSVars.black}; font-weight: 600; word-break: break-all; }
`;

export const StyledSummary = styled.p`
  max-width: 1040px;
  margin: 0 auto 10px;
  text-align: center;
  color: ${CSSVars.gray600};
  font-size: ${CSSVars.fontSizeMD};
  word-break: break-all;
  strong { color: ${CSSVars.black}; }
`;

export const StyledWarning = styled.p`
  max-width: 720px;
  margin: 0 auto 28px;
  background: #fff7e6;
  border: 1px solid #ffe2a8;
  color: #8a5a00;
  border-radius: 10px;
  padding: 12px 18px;
  font-size: ${CSSVars.fontSizeSM};
  line-height: 1.6;
  text-align: left;
`;

export const StyledShareBar = styled.div`
  max-width: 1040px;
  margin: 14px auto 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px 14px;

  button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: ${CSSVars.white};
    color: ${CSSVars.blue500};
    border: 1.5px solid ${CSSVars.blue500};
    border-radius: 10px;
    font-family: ${CSSVars.primaryFont};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: ${CSSVars.blue500};
      color: ${CSSVars.white};
    }
  }

  .status {
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeSM};
  }

  input {
    width: min(360px, 80vw);
    padding: 9px 12px;
    border: 1px solid ${CSSVars.gray200};
    border-radius: 8px;
    font-size: ${CSSVars.fontSizeSM};
    font-family: ${CSSVars.primaryFont};
    color: ${CSSVars.black};
  }
`;

// ── V2's animated scorecard ──────────────────────────────────────────────────
export const StyledScorecard = styled.section`
  max-width: 1040px;
  margin: 18px auto 52px;
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 22px;
  box-shadow: 0 12px 50px rgba(0, 0, 0, 0.08);
  padding: 32px 24px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  align-items: center;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: 240px 1fr;
    padding: 44px 48px;
    gap: 48px;
  }
`;

export const StyledDonut = styled.div<{ $status: 'poor' | 'fair' | 'good' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-self: center;

  .donut {
    position: relative;
    width: 200px;
    height: 200px;

    svg { width: 100%; height: 100%; transform: rotate(-90deg); }

    .track { stroke: ${CSSVars.gray200}; }
    .value {
      stroke: ${({ $status }) => STATUS_COLOR[$status]};
      stroke-linecap: round;
      transition: stroke-dashoffset 1.3s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .center {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .num {
        font-size: 3.25rem;
        font-weight: 700;
        line-height: 1;
        color: ${({ $status }) => STATUS_COLOR[$status]};
      }
      .denom {
        font-size: ${CSSVars.fontSizeSM};
        color: ${CSSVars.gray400};
        font-weight: 600;
        margin-top: 2px;
      }
    }
  }

  .label {
    margin-top: 16px;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: ${CSSVars.gray500};
  }
  .verdict {
    margin-top: 6px;
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 600;
    color: ${({ $status }) => STATUS_COLOR[$status]};
  }
`;

export const StyledBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

export const StyledBar = styled.div<{ $status: 'poor' | 'fair' | 'good'; $pct: number; $shown: boolean }>`
  .top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 7px;

    .name {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 600;
      color: ${CSSVars.gray700};
    }
    .val {
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 700;
      color: ${({ $status }) => STATUS_COLOR[$status]};
      font-variant-numeric: tabular-nums;
    }
  }

  .track {
    height: 12px;
    border-radius: 8px;
    background: ${CSSVars.gray100};
    overflow: hidden;

    .fill {
      height: 100%;
      border-radius: 8px;
      width: ${({ $shown, $pct }) => ($shown ? $pct : 0)}%;
      background: ${({ $status }) =>
        $status === 'good'
          ? 'linear-gradient(90deg, #16a34a, #4ade80)'
          : $status === 'fair'
          ? 'linear-gradient(90deg, #d28100, #fbbf24)'
          : 'linear-gradient(90deg, #dc2626, #f87171)'};
      transition: width 1.2s cubic-bezier(0.22, 1, 0.36, 1);
    }
  }

  .info {
    position: relative;
    display: inline-flex;
    align-items: center;
    color: ${CSSVars.gray400};
    cursor: help;
    outline: none;
    transition: color 0.15s;

    &:hover, &:focus-visible { color: ${CSSVars.blue500}; }
    svg { display: block; }

    .tooltip {
      position: absolute;
      bottom: calc(100% + 10px);
      left: 0;
      transform: translateY(4px);
      width: 240px;
      max-width: 72vw;
      background: ${CSSVars.black};
      color: ${CSSVars.white};
      text-align: left;
      font-size: 11.5px;
      font-weight: 400;
      line-height: 1.5;
      letter-spacing: 0;
      text-transform: none;
      padding: 10px 12px;
      border-radius: 10px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 0.15s, transform 0.15s;
      z-index: 20;
    }
    &:hover .tooltip, &:focus-visible .tooltip {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }
`;

export const StyledRecsHeader = styled.div`
  max-width: 1040px;
  margin: 0 auto 28px;
  text-align: center;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 8px;
    @media screen and (min-width: ${CSSVars.breakpointMD}) { font-size: ${CSSVars.fontSize2XL}; }
  }
  p { color: ${CSSVars.gray600}; font-weight: 300; font-size: ${CSSVars.fontSizeMD}; }
`;

export const StyledRecGrid = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
`;

// ── V3's content card + V2's impact meter ────────────────────────────────────
export const StyledRecCard = styled.article<{ $impact: 'high' | 'medium' | 'low' }>`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  padding: 26px 22px;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.05);
  border-top: 4px solid
    ${({ $impact }) => ($impact === 'high' ? '#dc2626' : $impact === 'medium' ? '#d28100' : CSSVars.blue500)};

  @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 32px; }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
  }
  .cat {
    display: inline-block;
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${CSSVars.blue500};
    background: rgba(76, 79, 255, 0.08);
    border-radius: 20px;
    padding: 4px 12px;
  }
  .impact {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ $impact }) => ($impact === 'high' ? '#dc2626' : $impact === 'medium' ? '#b45309' : CSSVars.gray500)};

    .dots { display: inline-flex; gap: 3px; }
    .dot { width: 7px; height: 7px; border-radius: 50%; background: ${CSSVars.gray200}; }
    .dot.on {
      background: ${({ $impact }) => ($impact === 'high' ? '#dc2626' : $impact === 'medium' ? '#d28100' : CSSVars.blue500)};
    }
  }

  h3 {
    font-size: ${CSSVars.fontSizeLG};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
    line-height: 1.3;
  }
  .finding {
    font-size: ${CSSVars.fontSizeMD};
    color: ${CSSVars.gray600};
    line-height: 1.65;
    font-weight: 300;
    margin-bottom: 24px;
  }

  .stat {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    background: linear-gradient(135deg, rgba(76, 79, 255, 0.06), rgba(255, 151, 156, 0.06));
    border: 1px solid rgba(76, 79, 255, 0.12);
    border-radius: 12px;
    padding: 16px 18px;
    margin-bottom: 24px;

    .num { font-size: 1.9rem; font-weight: 700; color: ${CSSVars.blue500}; line-height: 1; white-space: nowrap; }
    .body { flex: 1; }
    .claim { font-size: ${CSSVars.fontSizeSM}; color: ${CSSVars.gray700}; line-height: 1.55; }
    .src {
      display: inline-block;
      margin-top: 6px;
      font-size: ${CSSVars.fontSizeXS};
      color: ${CSSVars.gray500};
      a { color: ${CSSVars.blue500}; text-decoration: underline; }
    }
  }

  .gallery-label {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 12px;
  }

  .tools {
    margin-top: 22px;
    border-top: 1px dashed rgba(0, 0, 0, 0.1);
    padding-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;

    .tools-label {
      font-size: ${CSSVars.fontSizeXS};
      color: ${CSSVars.gray500};
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      width: 100%;
    }
    a {
      font-size: ${CSSVars.fontSizeSM};
      font-weight: 600;
      color: ${CSSVars.blue500};
      text-decoration: none;
      border: 1px solid rgba(76, 79, 255, 0.25);
      border-radius: 8px;
      padding: 7px 13px;
      transition: background 0.2s, color 0.2s;
      &:hover { background: ${CSSVars.blue500}; color: ${CSSVars.white}; }
    }
  }
`;

export const StyledWidgetGallery = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media screen and (min-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledWidgetTile = styled.a`
  display: block;
  text-decoration: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  overflow: hidden;
  background: ${CSSVars.white};
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  .thumb {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 10;
    background: linear-gradient(135deg, #eef0ff, #fdeef0);
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.4s ease;
    }

    .fallback {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        width: 56px;
        height: 56px;
        object-fit: contain;
        border-radius: 12px;
        background: ${CSSVars.white};
        padding: 8px;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
        transition: none;
      }
    }
  }

  .caption {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 14px;

    .name { font-size: ${CSSVars.fontSizeSM}; font-weight: 700; color: ${CSSVars.black}; }
    .go { font-size: ${CSSVars.fontSizeXS}; font-weight: 700; color: ${CSSVars.blue500}; white-space: nowrap; }
  }

  &:hover {
    border-color: rgba(76, 79, 255, 0.45);
    box-shadow: 0 10px 30px rgba(76, 79, 255, 0.15);
    transform: translateY(-2px);
    .thumb img { transform: scale(1.05); }
  }
`;
